import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle, XCircle, Clock, Loader2, RefreshCw, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import { getWorkflowExecutions, WorkflowExecution } from '../../services/workflowService';
import { MARKETPLACE_EVENT_LABELS } from './MarketplaceConnectionPanel';

interface Props {
  workflowId: string;
  nodes: Array<{ id: string; data?: { label?: string; type?: string } }>;
  isOpen: boolean;
  onClose: () => void;
}

const STATUS: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
  completed: { label: 'Deu certo', cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: <CheckCircle className="h-3.5 w-3.5" /> },
  failed: { label: 'Falhou', cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', icon: <XCircle className="h-3.5 w-3.5" /> },
  running: { label: 'Rodando', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: <Loader2 className="h-3.5 w-3.5 animate-spin" /> },
  waiting: { label: 'Esperando', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', icon: <Clock className="h-3.5 w-3.5" /> },
  paused: { label: 'Pausado', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', icon: <Clock className="h-3.5 w-3.5" /> },
};

const PARTIAL = { label: 'Deu certo em parte', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', icon: <AlertTriangle className="h-3.5 w-3.5" /> };

type Step = { nodeId: string; label: string; ok: boolean; error?: string; detail?: string };

// Junta o registro bruto (node_started / node_completed / node_error) em uma linha por passo
const stepsOf = (exec: WorkflowExecution, nodes: Props['nodes']): Step[] => {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const out: Step[] = [];
  for (const e of exec.execution_log || []) {
    if (e.action !== 'node_completed' && e.action !== 'node_error') continue;
    const nodeId = String(e.nodeId || '');
    const node = byId.get(nodeId);
    const label = node?.data?.label || String(e.nodeType || nodeId);
    const output = (e.output || {}) as Record<string, any>;
    const ok = e.action === 'node_completed' ? e.success !== false : false;
    const error = e.action === 'node_error' ? String(e.error || '') : (ok ? undefined : String(output.error || e.error || 'Este passo não deu certo.'));
    let detail: string | undefined;
    if (ok && output) {
      if (output.url) detail = String(output.url);
      else if (output.sent_to) detail = `E-mail para ${output.sent_to}`;
      else if (output.order_number) detail = `Pedido ${output.order_number}${output.status ? ` · ${output.status}` : ''}`;
      else if (Array.isArray(output.tags)) detail = `Etiquetas: ${output.tags.join(', ')}`;
    }
    out.push({ nodeId, label, ok, error, detail });
  }
  return out;
};

const WorkflowRunLog: React.FC<Props> = ({ workflowId, nodes, isOpen, onClose }) => {
  const [rows, setRows] = useState<WorkflowExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await getWorkflowExecutions(workflowId, { limit: 20 });
      setRows(r.data || []);
      if (r.data && r.data[0]) setOpen((prev) => prev || r.data[0].id);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Não consegui carregar as execuções.');
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Execuções deste fluxo" description="As últimas 20 vezes que o fluxo rodou, passo a passo." size="2xl" maxHeight>
      <div className="space-y-3" data-testid="run-log">
        <div className="flex justify-end">
          <button type="button" onClick={load} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Atualizar
          </button>
        </div>
        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{error}</p>}
        {!loading && !error && rows.length === 0 && (
          <p className="py-8 text-center text-sm text-gray-500">O fluxo ainda não rodou. Quando rodar, cada passo aparece aqui.</p>
        )}
        {rows.map((exec) => {
          const steps = stepsOf(exec, nodes);
          const failed = steps.filter((s) => !s.ok);
          // Rodou até o fim mas algum passo falhou: não é "deu certo"
          const st = exec.status === 'completed' && failed.length ? PARTIAL : (STATUS[exec.status] || STATUS.paused);
          const event = String((exec.trigger_data as any)?.event || '');
          const who = (exec.lead_data as any)?.name || (exec.lead_data as any)?.email || '';
          const isOpenRow = open === exec.id;
          return (
            <div key={exec.id} className="rounded-xl border border-gray-200 dark:border-gray-700">
              <button type="button" onClick={() => setOpen(isOpenRow ? null : exec.id)} className="flex w-full items-center gap-3 px-3 py-2.5 text-left">
                {isOpenRow ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${st.cls}`}>{st.icon} {st.label}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-gray-800 dark:text-gray-200">
                  {event ? (MARKETPLACE_EVENT_LABELS[event] || event) : 'Início'}{who ? ` · ${who}` : ''}
                </span>
                {failed.length > 0 && <span className="text-xs font-medium text-red-600">{failed.length} com erro</span>}
                <span className="flex-shrink-0 text-xs text-gray-500">{new Date(exec.started_at || exec.created_at).toLocaleString('pt-BR')}</span>
              </button>
              {isOpenRow && (
                <div className="space-y-1.5 border-t border-gray-100 px-3 py-2.5 dark:border-gray-700">
                  {exec.error_message && (
                    <p className="flex items-start gap-1.5 rounded-lg bg-red-50 px-2.5 py-1.5 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> {exec.error_message}
                    </p>
                  )}
                  {steps.length === 0 && <p className="text-xs text-gray-500">Sem passos registrados.</p>}
                  {steps.map((s, i) => (
                    <div key={`${s.nodeId}-${i}`} className="flex items-start gap-2 text-sm">
                      {s.ok ? <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" /> : <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />}
                      <div className="min-w-0">
                        <p className="text-gray-800 dark:text-gray-200">{s.label}</p>
                        {s.error && <p className="text-xs text-red-600 dark:text-red-400" data-testid="run-step-error">{s.error}</p>}
                        {s.detail && <p className="truncate text-xs text-gray-500">{s.detail}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default WorkflowRunLog;
